/**
 *
 * "Post-Shut Report page for viewing and interact Post-Shut
 * Reprot Form and export Report in a .DocX file."
 *
 * @file   PartsPage.js
 * @author Lateral
 * @since  2023
 */
import { Box } from '@mui/material'
import React, { useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { useCurrentUser, useMaintenance } from 'hooks'
import dayjs from 'dayjs'
import PostShutForm from './components/postShut/PostShutForm'
import {
  companyName,
  crewDetailsName,
  introductionName,
  recentJobsName,
  recommendationsName,
  safetyIssuesName,
  scopeOfWorkName,
  shutdownCommentsName,
  shutEndDateName,
  shutStartDateName,
  siteContactName,
  siteName,
  siteRepresentativeName,
  titleName
} from './common/formNames'
import { toPng } from 'html-to-image'
import BigNumber from 'bignumber.js'
import { Storage } from 'aws-amplify'
import { CrewDetailsItems } from 'models'
import { createReport as createReportModel } from './common/data'
import { reportTypes } from './common/reportTypes'
import { useReportingContext } from 'components/reportingContext/ReportingContext'
import { dateFormat } from 'common/dates'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { blobToBase64 } from 'common/converters'
import { getBillOfMaterial } from './common/generationBill'
import { useBackgroundProcess } from 'context/BackgroundProcessContext'
import { chunk, groupBy } from 'common/arrays'

function PostShutPage() {
  /**
   * Generates Post-shut Report page with Post-Shut
   * Report Form to view on desktop sized screen
   *
   * @function
   *
   * @returns {object} - Post-Shut Report page
   */
  const theme = useTheme()
  const { actions, getAction } = useMaintenance()
  const { database } = useCurrentDeckContext()
  const { existingReport } = useReportingContext()
  const { getTransactionMetaData } = useCurrentUser()
  const { addProcess } = useBackgroundProcess()
  const [isCreatingReport, setIsCreatingReport] = useState(false)

  const getDeckRevisionWithLatestKitInfo = (deckId, kitMaterialNumber) => {
    const revisions = database.deckRevisions
      .filter((d) => d.DeckId == deckId && d.IsPublished)
      .sort((a, b) => b.RevisionNumber - a.RevisionNumber)

    for (const deckRevision of revisions) {
      const kit = deckRevision.Kits.find((k) => k.MaterialNumber === kitMaterialNumber)
      if (kit) {
        return kit
      }
    }
  }

  //generate the DocX
  async function onSubmit(form) {
    setIsCreatingReport(true)
    const customer = database.customers.find((c) => c.id === form[companyName])
    const site = database.sites.find((s) => s.id === form[siteName])
    const photoSize = 8
    const base64OctetPrepend = 'data:binary/octet-stream;base64,'
    const title = form[titleName] ? form[titleName] : customer.Name

    const model = existingReport?.Type === reportTypes.PostShut ? structuredClone(existingReport) : {}
    model.Type = reportTypes.PostShut
    model.CustomerId = customer.id
    model.Company = customer.Name
    model.Title = title
    model.SiteId = site.id
    model.Site = site.Name
    model.DateOfVisit = dayjs(form[shutStartDateName]).toISOString()
    model.EndDate = dayjs(form[shutEndDateName]).toISOString()
    model.SiteContact = JSON.stringify(form[siteContactName])
    model.SiteRepresentative = JSON.stringify(form[siteRepresentativeName])
    model.Introduction = form[introductionName]
    model.ScopeOfWork = form[scopeOfWorkName]
    model.SafetyIssues = form[safetyIssuesName]
    model.CrewDetails = form[crewDetailsName].map(
      (c) =>
        new CrewDetailsItems({
          Shift: c.Shift,
          Name: c.Name,
          Position: c.Position
        })
    )
    model.ShutdownComments = form[shutdownCommentsName]
    model.Recommendations = form[recommendationsName]
    model.Selections = form[recentJobsName].filter((j) => j.Include).map((j) => j.HistoryId)

    if (!model.Revision) {
      model.Revision = 0
    }
    if (existingReport && JSON.stringify(existingReport) !== JSON.stringify(model)) {
      model.Revision += 1
    }

    //create/update the report, but only if something changed.
    if (!existingReport || model.Revision !== existingReport?.Revision) {
      await createReportModel(model, getTransactionMetaData())
    }

    const mapToCrewFormat = (data) =>
      data.map((item) => ({
        Shift: item.Shift,
        Name: item.User?.name ?? item.Name,
        Position: item.Position
      }))
    //gather data
    let data = {
      CustomerId: customer.id,
      Company: customer.Name,
      Site: site.Name,
      Title: title,
      DateOfVisit: dayjs(form[shutStartDateName]).format(dateFormat),
      SiteContact: mapToCrewFormat(form[siteContactName]),
      contact_information: chunk(form[siteRepresentativeName], 2).map((contacts) => {
        return contacts.map((contact, index) => {
          return { name: contact.User.name, email: contact.User.email, position: contact.Position, index }
        })
      }),
      contact_information_night: form[siteRepresentativeName]
        .filter((contact) => contact.Shift === 'Night')
        .map((contact) => {
          return { name: contact.User.name }
        }),
      contact_information_Day: form[siteRepresentativeName]
        .filter((contact) => contact.Shift === 'Day')
        .map((contact) => {
          return { name: contact.User.name }
        }),
      SiteRepresentative: mapToCrewFormat(form[siteRepresentativeName]),
      Introduction: form[introductionName],
      ScopeOfWork: form[scopeOfWorkName],
      SafetyIssues: form[safetyIssuesName],
      CrewDetails: form[crewDetailsName],
      ShutdownComments: form[shutdownCommentsName],
      Recommendations: form[recommendationsName],
      Inspections: await Promise.all(
        form[recentJobsName]
          .filter((f) => f.Include)
          .map(async (job) => {
            const history = database.deckRevisionHistories.find((d) => d.id === job.HistoryId)
            const deckRevision = database.deckRevisions.find((d) => d.id === history.RevisionId)
            const deck = database.decks.find((d) => d.id === deckRevision.DeckId)
            const location = database.locations.find((l) => l.id === deck.LocationId)
            const screen = database.screens.find((s) => s.id === deck.ScreenId)

            //show, render to PNG, then hide the hidden canvases
            job.htmlElement.hidden = false
            const postLayout = await toPng(job.htmlElement)

            job.defaultElement.hidden = false
            const defaultLayout = await toPng(job.defaultElement)
            job.defaultElement.hidden = true

            let preLayout = ''

            if (job.preElement) {
              job.preElement.hidden = false
              preLayout = await toPng(job.preElement)
            }

            const itemList = {}
            history.Details.forEach((detail) => {
              const materialNumber = detail.Panel?.MaterialNumber ?? detail.SideLiner?.MaterialNumber
              const kit = getDeckRevisionWithLatestKitInfo(deck.id, materialNumber)

              if (materialNumber && detail.HistoryAction !== actions.NoChange.id) {
                const similarDetails = history.Details.filter(
                  (d) =>
                    (d.Panel?.MaterialNumber === materialNumber || d.SideLiner?.MaterialNumber === materialNumber) &&
                    d.HistoryAction === detail.HistoryAction
                )
                const key = `${materialNumber}-${detail.HistoryAction}-${similarDetails.length}`

                if (!itemList[key]) {
                  const action = getAction(detail.HistoryAction)
                  itemList[key] = {
                    MaterialNumber: kit.CustomerStockCode,
                    HistoryAction: detail.HistoryAction,
                    HistoryState: action.state,
                    SchenckNumber: screen?.SchenckSerial ?? 'N/A',
                    PartNumber: kit.PartNumber,
                    Description: kit.MaterialDescription ? kit.MaterialDescription : kit.MaterialNumber,
                    Quantity: similarDetails.length,
                    ScreenName: `${screen?.Name} - ${deck.DeckHeader.DeckLevel}`,
                    key
                  }
                }
              }
            })
            history.OtherKitsMaintenance?.forEach((otherKit) => {
              const kit = getDeckRevisionWithLatestKitInfo(deck.id, otherKit.MaterialNumber)
              if (otherKit.MaterialNumber && otherKit.HistoryAction !== actions.NoChange.id) {
                const key = `${otherKit.MaterialNumber}-${otherKit.HistoryAction}-otherkits`

                if (!itemList[key]) {
                  const action = getAction(actions.Replace.id)
                  itemList[key] = {
                    MaterialNumber: otherKit.MaterialNumber,
                    HistoryAction: actions.Replace.id,
                    HistoryState: action.state,
                    SchenckNumber: screen?.SchenckSerial ?? 'N/A',
                    PartNumber: kit?.PartNumber || 'N/A',
                    Description: kit?.MaterialDescription ? kit.MaterialDescription : otherKit.MaterialNumber,
                    Quantity: otherKit.Quantity,
                    key
                  }
                }
              }
            })
            const photoPairs = {
              preService: {
                photoPairs: [],
                visibility: 0
              },
              midService: {
                photoPairs: [],
                visibility: 0
              },
              postService: {
                photoPairs: [],
                visibility: 0
              }
            }
            let photo_visibility = 0
            //gather photos
            if (history.Photos) {
              const preServicePhotos = history.Photos.filter((photo) => {
                return (
                  photo.Key.includes('-pre-service-') ||
                  !['-mid-service-', '-post-service-', '-pre-service-'].some((key) => !key.includes(photo.Key))
                )
              })
              const midServicePhotos = history.Photos.filter((photo) => {
                return photo.Key.includes('-mid-service-')
              })
              const postServicePhotos = history.Photos.filter((photo) => {
                return photo.Key.includes('-post-service-')
              })

              const photos = {
                preService: preServicePhotos,
                midService: midServicePhotos,
                postService: postServicePhotos
              }

              for (const key in photos) {
                const photos_ = photos[key]
                //we have to add photos 2 at a time, due to alignment issues in docx files.
                for (let i = 0; i < photos_.length; i += 2) {
                  const photoKey1 = photos_[i]
                  const photo1Model = await getPhotoModel(photoKey1, photoSize, base64OctetPrepend)
                  let photo2Model = null

                  if (i + 1 < photos_.length) {
                    const photoKey2 = photos_[i + 1]
                    photo2Model = await getPhotoModel(photoKey2, photoSize, base64OctetPrepend)
                  }
                  const pairs = []
                  if (photo1Model) {
                    pairs.push(photo1Model)
                  }

                  if (photo2Model) {
                    pairs.push(photo2Model)
                  }

                  if (photo1Model || photo2Model) {
                    photo_visibility = 1
                    photoPairs[key].photoPairs.push(pairs)
                    photoPairs[key].visibility = 1
                  }
                }
              }
            }

            return {
              Name_order: `${location.Name} - ${screen?.Name} - ${deck.DeckHeader.EquipmentId}`,
              Name: `${deck.DeckHeader.EquipmentId}`,
              Deck_Name: deck.DeckHeader.DeckLevel,
              Location: location.Name,

              Equipment: deck.DeckHeader.EquipmentId,
              ScreenNumber: screen?.Name ?? 'N/A',
              SchenckNumber: screen?.SchenckSerial ?? 'N/A',
              MaterialNumber: deck.DeckHeader.MaterialNumber,
              Tonnage: history.Tonnage,
              Summary: history.Description,
              PreServiceComments: history.PreServiceComments,
              PostServiceComments: history.PostServiceComments,
              DeckRailsComments: history.DeckRailsComments,
              CrossbeamComments: history.CrossbeamComments,
              Recommendations: history.Recommendations,
              ItemsChanged: Object.values(itemList),
              Bill_of_Materials: getBillOfMaterial(deckRevision),
              Bill_of_Materials_Itemized: getBillOfMaterial(deckRevision, true),
              // add photos:
              Deck_Layout_Picture: defaultLayout,
              Deck_Actions_Image: postLayout,
              Wear_Measurement_Image: preLayout,
              // photo_column1: photo_column1,
              // photo_column2: photo_column2,
              photoPairs: photoPairs,
              visibility: photo_visibility
            }
          })
      ),
      wearReport: form['wearReport'],
      ItemsSummary: []
    }

    const summaryShutdownScreens = []

    //generate item summary
    data.Inspections.forEach((inspection) => {
      inspection.ItemsChanged.forEach((item) => {
        if (item.HistoryAction === actions.Replace.id || item.HistoryAction === actions.Relocate.id) {
          const existing = data.ItemsSummary.find((i) => {
            if (item.MaterialNumber && item.SchenckNumber) {
              return i.MaterialNumber === item.MaterialNumber && i.SchenckNumber === item.SchenckNumber
            } else if (item.MaterialNumber) {
              return i.MaterialNumber === item.MaterialNumber
            } else if (item.Description) {
              return i.Description === item.Description
            }
            return i.key === item.key
          })
          if (existing) {
            existing.Quantity = new BigNumber(existing.Quantity).plus(item.Quantity).toNumber()
          } else {
            data.ItemsSummary.push(structuredClone(item))
            summaryShutdownScreens.push(structuredClone(item))
          }
        }
      })
    })
    const groupedSummaryShutdownScreens = groupBy('ScreenName')(summaryShutdownScreens)
    const groupedSummaryShutdownScreensData = Object.keys(groupedSummaryShutdownScreens).map((screenGroup, index) => {
      const groupedItems = groupedSummaryShutdownScreens[screenGroup]
      return {
        screen_number: index + 1,
        deck_name: screenGroup,
        shutdown_parts: groupedItems.map((item) => ({
          MaterialNumber: item.MaterialNumber,
          PartNumber: item.PartNumber,
          Description: item.Description,
          Quantity: item.Quantity
        }))
      }
    })

    data.summary_shutdown_screen = groupedSummaryShutdownScreensData
    data.itemized_bom = form['itemizedBOM']

    // Sorting inspections by Name first, then Deck order (Top → Middle → Bottom)
    const deckOrder = { TopDeck: 1, MiddleDeck: 2, BottomDeck: 3 }

    data.Inspections.sort((a, b) => {
      const localeCompare = a.Equipment.localeCompare(b.Equipment)

      if (localeCompare !== 0) {
        return localeCompare
      }

      // Handle Deck_Name ordering (Top -> Middle -> Bottom)
      const deckA = deckOrder[a.Deck_Name] || 999 // Default to a high number if unknown
      const deckB = deckOrder[b.Deck_Name] || 999

      return deckA - deckB
    })

    //generate the report
    // console.log('Before calling addProcess:', {
    //   data: data
    // })
    try {
      await addProcess({
        customerId: data.CustomerId,
        type: 'report',
        data,
        reportType: 'postShut'
      })
    } catch {
      //ingored
    }
    setIsCreatingReport(false)
  }

  async function getPhotoModel(photoModel, photoSize, base64OctetPrepend) {
    try {
      const photo = await Storage.get(photoModel.Key.replace('public/', ''), { download: true })
      const body = await blobToBase64(photo.Body)
      return {
        Photo: `data:image/png;base64,${body.slice(base64OctetPrepend.length)}`,
        Caption: photoModel.Caption
      }
    } catch {
      // ignored
    }
    return null
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 200px)',
        overflow: 'hidden',
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.supporting.dark
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.supporting.pale,
          borderRadius: 2
        }
      }}
      display="flex"
      flexDirection="column"
      key="post-shut-page">
      <PostShutForm
        data={existingReport}
        customers={database.customers}
        sites={database.sites}
        locations={database.locations}
        decks={database.decks}
        deckRevisions={database.deckRevisions}
        deckRevisionHistories={database.deckRevisionHistories}
        onSubmit={onSubmit}
        isCreatingReport={isCreatingReport}
      />
    </Box>
  )
}
export default PostShutPage
